'use client';

import { useEffect, useState, type RefObject } from 'react';
import { stripHtml } from '@/lib/homepage-mappers';
import type { PncWonderTestimonial } from '@/types/people-and-culture';
import { TESTIMONIAL_COLORS } from '../lib/people-and-culture.constants';

interface TestimonialsSectionProps {
  sectionRef: RefObject<HTMLDivElement>;
  zone: PncWonderTestimonial;
}

export default function TestimonialsSection({ sectionRef, zone }: TestimonialsSectionProps) {
  const testimonials = zone.wonderTestimonialItem ?? [];
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    if (testimonials.length <= 1) return undefined;
    const id = setInterval(() => {
      setActiveTestimonial((i) => (i + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(id);
  }, [testimonials.length]);

  if (testimonials.length === 0) return null;

  const active = testimonials[activeTestimonial];
  const activeColor = TESTIMONIAL_COLORS[activeTestimonial % TESTIMONIAL_COLORS.length];
  const quote = active.testimonial ? stripHtml(active.testimonial).replace(/(?:^")|("$)/g, '') : '';

  return (
    <section ref={sectionRef} className="bg-[#f8f8f7] px-4 sm:px-6 md:px-16 lg:px-24">
      <div data-reveal className="mb-6 sm:mb-8">
        <h2 className="text-3xl font-bold text-[#0a0a0a] sm:text-4xl md:text-5xl">
          {zone.heading}
        </h2>
      </div>

      <div className="flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-stretch">
        <div
          data-test-panel
          className="flex flex-1 flex-col justify-between rounded-2xl bg-white p-6 shadow-sm sm:p-8"
        >
          <div>
            <span className="mb-5 block text-4xl font-bold leading-none text-[#ed1c22]/30">
              &ldquo;&rdquo;
            </span>
            <p className="text-base font-medium leading-relaxed text-[#1a1a1a] sm:text-lg">
              {quote}
            </p>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: activeColor }}
            >
              {active.authorInitials}
            </div>
            <div>
              <p className="font-semibold text-[#0a0a0a]">{active.authorName}</p>
              <p className="text-sm text-gray-400">{active.authorDesg}</p>
            </div>
          </div>
        </div>

        <div data-test-panel className="flex w-full flex-col gap-3 lg:w-[38%] lg:shrink-0">
          {testimonials.map((testimonial, i) => {
            const color = TESTIMONIAL_COLORS[i % TESTIMONIAL_COLORS.length];
            return (
              <button
                key={testimonial.id}
                type="button"
                onClick={() => setActiveTestimonial(i)}
                className={`flex w-full flex-1 items-center gap-4 rounded-2xl bg-white px-5 py-5 text-left shadow-sm transition-all duration-200 ${
                  i === activeTestimonial
                    ? 'ring-1 ring-[#ed1c22]/40 bg-[#fff7f7]'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: color }}
                >
                  {testimonial.authorInitials}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#0a0a0a]">
                    {testimonial.authorName}
                  </p>
                  <p className="truncate text-xs text-gray-400">{testimonial.authorDesg}</p>
                </div>

                {i === activeTestimonial && (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#ed1c22]" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
